import { useState, useRef, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuth } from "@/contexts/AuthContext";
import { AppShell3 } from "@/components/app-shell-3";
import { ToolHeader } from '@/components/ToolHeader';
import {
    Camera,
    Upload,
    Lock,
    Key,
    CheckCircle,
    XCircle,
    User,
    Phone,
    FileText,
    Bell,
    Shield,
    Save,
    Loader2,
    ChevronLeft
} from "lucide-react";
import { ImageCropper } from "@/components/ImageCropper";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { supabase } from "@/lib/supabase";
import { gcsUploadFile, gcsGetPublicUrl } from "@/lib/gcsStorage";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { NotificationPreferences } from '@/components/NotificationPreferences';
import { AlertMutingCard } from '@/components/AlertMutingCard';
import { PageTransition } from '@/components/PageTransition';
import KioskPinManager from '@/components/KioskPinManager';

// Navigation sections configuration
const SECTIONS = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'personal', label: 'Personal Information', icon: User },
    { id: 'emergency', label: 'Emergency Contact', icon: Phone },
    { id: 'documents', label: 'ID Documents', icon: FileText },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
];

export function SettingsPage() {
    const { user, userProfile, company, updateProfile, refreshUserData, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    usePageTitle('Settings');
    const [isSaving, setIsSaving] = useState(false);

    // Section navigation
    const [activeSection, setActiveSection] = useState('profile');

    // Profile images
    const [profileImage, setProfileImage] = useState(null);
    const [previewProfile, setPreviewProfile] = useState(null);

    // Form fields
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [jobTitle, setJobTitle] = useState("");
    const [location, setLocation] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [employmentStartDate, setEmploymentStartDate] = useState("");

    // Personal/Compliance fields
    const [niNumber, setNiNumber] = useState("");
    const [dateOfBirth, setDateOfBirth] = useState("");

    // ID Document fields
    const [passportNumber, setPassportNumber] = useState("");
    const [passportExpiry, setPassportExpiry] = useState("");
    const [passportFileUrl, setPassportFileUrl] = useState("");
    const [passportFilePreview, setPassportFilePreview] = useState(null);
    const [drivingLicenseNumber, setDrivingLicenseNumber] = useState("");
    const [drivingLicenseExpiry, setDrivingLicenseExpiry] = useState("");
    const [drivingLicenseFileUrl, setDrivingLicenseFileUrl] = useState("");
    const [drivingLicenseFilePreview, setDrivingLicenseFilePreview] = useState(null);

    // Medical and Right to Work
    const [medicalInfo, setMedicalInfo] = useState("");
    const [rightToWorkStatus, setRightToWorkStatus] = useState("");
    const [rightToWorkExpiry, setRightToWorkExpiry] = useState("");
    const [rightToWorkFileUrl, setRightToWorkFileUrl] = useState("");
    const [rightToWorkFilePreview, setRightToWorkFilePreview] = useState(null);

    // Emergency contact fields
    const [emergencyContactName, setEmergencyContactName] = useState("");
    const [emergencyContactRelationship, setEmergencyContactRelationship] = useState("");
    const [emergencyContactHomeAddress, setEmergencyContactHomeAddress] = useState("");
    const [emergencyContactHomePhone, setEmergencyContactHomePhone] = useState("");
    const [emergencyContactMobilePhone, setEmergencyContactMobilePhone] = useState("");
    const [emergencyContactEmail, setEmergencyContactEmail] = useState("");
    const [emergencyContactEmployer, setEmergencyContactEmployer] = useState("");
    const [emergencyContactWorkPhone, setEmergencyContactWorkPhone] = useState("");

    // Password fields
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [passwordSuccess, setPasswordSuccess] = useState(false);
    const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

    // Image cropper states
    const [showProfileCropper, setShowProfileCropper] = useState(false);
    const [tempProfileImage, setTempProfileImage] = useState(null);

    const profileInputRef = useRef(null);
    const passportInputRef = useRef(null);
    const drivingLicenseInputRef = useRef(null);
    const rightToWorkInputRef = useRef(null);

    const userEmail = user?.email || "user@example.com";
    const displayName = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || userProfile?.first_name || userProfile?.last_name || "User";
    const avatarUrl = previewProfile || profileImage || userProfile?.avatar_url;

    const getInitials = (name) => {
        return name
            ?.split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2) || 'U';
    };

    const scrollToSection = (sectionId) => {
        setActiveSection(sectionId);
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // Track active section on scroll
    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY + 150;

            for (const section of SECTIONS) {
                const element = document.getElementById(section.id);
                if (element) {
                    const { offsetTop, offsetHeight } = element;
                    if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
                        setActiveSection(section.id);
                        break;
                    }
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Update form fields when userProfile changes
    useEffect(() => {
        if (userProfile) {
            setFirstName(userProfile.first_name || "");
            setLastName(userProfile.last_name || "");
            setJobTitle(userProfile.job_title || "");
            setLocation(userProfile.location || "");
            setPhoneNumber(userProfile.phone_number || "");
            setEmploymentStartDate(userProfile.employment_start_date || "");
            setNiNumber(userProfile.ni_number || "");
            setDateOfBirth(userProfile.date_of_birth || "");
            setPassportNumber(userProfile.passport_number || "");
            setPassportExpiry(userProfile.passport_expiry || "");
            setPassportFileUrl(userProfile.passport_file_url || "");
            setDrivingLicenseNumber(userProfile.driving_license_number || "");
            setDrivingLicenseExpiry(userProfile.driving_license_expiry || "");
            setDrivingLicenseFileUrl(userProfile.driving_license_file_url || "");
            setMedicalInfo(userProfile.medical_info?.notes || "");
            setRightToWorkStatus(userProfile.right_to_work?.status || "");
            setRightToWorkExpiry(userProfile.right_to_work?.expiry_date || "");
            setRightToWorkFileUrl(userProfile.right_to_work_file_url || "");
            setEmergencyContactName(userProfile.emergency_contact_name || "");
            setEmergencyContactRelationship(userProfile.emergency_contact_relationship || "");
            setEmergencyContactHomeAddress(userProfile.emergency_contact_home_address || "");
            setEmergencyContactHomePhone(userProfile.emergency_contact_home_phone || "");
            setEmergencyContactMobilePhone(userProfile.emergency_contact_mobile_phone || "");
            setEmergencyContactEmail(userProfile.emergency_contact_email || "");
            setEmergencyContactEmployer(userProfile.emergency_contact_employer || "");
            setEmergencyContactWorkPhone(userProfile.emergency_contact_work_phone || "");
            setProfileImage(userProfile.avatar_url || user?.user_metadata?.avatar_url || null);
        }
    }, [userProfile, user]);

    const handleProfileImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setTempProfileImage(reader.result);
                setShowProfileCropper(true);
            };
            reader.onerror = () => {
                toast.error('Failed to read image file');
            };
            reader.readAsDataURL(file);
        }
    };

    const handleProfileCropComplete = (croppedImage) => {
        setPreviewProfile(croppedImage);
        setShowProfileCropper(false);
        setTempProfileImage(null);
    };

    const handleDocumentUpload = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                toast.error('File size must be less than 10MB');
                return;
            }

            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
            if (!allowedTypes.includes(file.type)) {
                toast.error('Only PDF and image files (JPG, PNG) are allowed');
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                if (type === 'passport') {
                    setPassportFilePreview({ data: reader.result, name: file.name, type: file.type });
                } else if (type === 'license') {
                    setDrivingLicenseFilePreview({ data: reader.result, name: file.name, type: file.type });
                } else if (type === 'rightToWork') {
                    setRightToWorkFilePreview({ data: reader.result, name: file.name, type: file.type });
                }
            };
            reader.onerror = () => {
                toast.error('Failed to read file');
            };
            reader.readAsDataURL(file);
        }
    };

    const uploadImageToSupabase = async (base64Image, fileName, bucket = 'avatars') => {
        try {
            const base64Data = base64Image.split(',')[1];
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const mimeMatch = base64Image.match(/^data:(.+);base64,/);
            const mimeType = mimeMatch ? mimeMatch[1] : 'image/jpeg';
            const blob = new Blob([byteArray], { type: mimeType });

            // Upload to GCS using the bucket name as the folder prefix
            const result = await gcsUploadFile(blob, {
                folderPath: bucket,
                fileName,
                contentType: mimeType,
            });

            return `${result.publicUrl}?t=${Date.now()}`;
        } catch (error) {
            console.error('Error uploading image:', error);
            toast.error(`Failed to upload image: ${error.message}`);
            throw error;
        }
    };

    const handleSaveProfile = async () => {
        setIsSaving(true);

        try {
            const updates = {
                first_name: firstName,
                last_name: lastName,
                job_title: jobTitle,
                location: location,
                phone_number: phoneNumber,
                employment_start_date: employmentStartDate || null,
                ni_number: niNumber,
                date_of_birth: dateOfBirth || null,
                passport_number: passportNumber,
                passport_expiry: passportExpiry || null,
                driving_license_number: drivingLicenseNumber,
                driving_license_expiry: drivingLicenseExpiry || null,
                medical_info: medicalInfo ? { notes: medicalInfo } : {},
                right_to_work: {
                    status: rightToWorkStatus,
                    expiry_date: rightToWorkExpiry || null
                },
                emergency_contact_name: emergencyContactName,
                emergency_contact_relationship: emergencyContactRelationship,
                emergency_contact_home_address: emergencyContactHomeAddress,
                emergency_contact_home_phone: emergencyContactHomePhone,
                emergency_contact_mobile_phone: emergencyContactMobilePhone,
                emergency_contact_email: emergencyContactEmail,
                emergency_contact_employer: emergencyContactEmployer,
                emergency_contact_work_phone: emergencyContactWorkPhone
            };

            if (previewProfile) {
                const profileFileName = `${userProfile?.id || user?.id}/avatar-${Date.now()}.jpg`;
                const profileUrl = await uploadImageToSupabase(previewProfile, profileFileName, 'avatars');
                updates.avatar_url = profileUrl;
            }

            if (passportFilePreview) {
                const ext = passportFilePreview.name.split('.').pop();
                const passportFileName = `${userProfile?.id || user?.id}/passport-${Date.now()}.${ext}`;
                const passportUrl = await uploadImageToSupabase(passportFilePreview.data, passportFileName, 'documents');
                updates.passport_file_url = passportUrl;
            }

            if (drivingLicenseFilePreview) {
                const ext = drivingLicenseFilePreview.name.split('.').pop();
                const licenseFileName = `${userProfile?.id || user?.id}/license-${Date.now()}.${ext}`;
                const licenseUrl = await uploadImageToSupabase(drivingLicenseFilePreview.data, licenseFileName, 'documents');
                updates.driving_license_file_url = licenseUrl;
            }

            if (rightToWorkFilePreview) {
                const ext = rightToWorkFilePreview.name.split('.').pop();
                const rtwFileName = `${userProfile?.id || user?.id}/right-to-work-${Date.now()}.${ext}`;
                const rtwUrl = await uploadImageToSupabase(rightToWorkFilePreview.data, rtwFileName, 'documents');
                updates.right_to_work_file_url = rtwUrl;
            }

            await updateProfile(updates);

            if (updates.avatar_url) setProfileImage(updates.avatar_url);
            if (updates.passport_file_url) setPassportFileUrl(updates.passport_file_url);
            if (updates.driving_license_file_url) setDrivingLicenseFileUrl(updates.driving_license_file_url);
            if (updates.right_to_work_file_url) setRightToWorkFileUrl(updates.right_to_work_file_url);

            setPreviewProfile(null);
            setPassportFilePreview(null);
            setDrivingLicenseFilePreview(null);
            setRightToWorkFilePreview(null);

            toast.success('Profile updated successfully!');
        } catch (error) {
            console.error('Error saving profile:', error);
            toast.error('Failed to save profile. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handlePasswordChange = async () => {
        setPasswordError("");
        setPasswordSuccess(false);

        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordError("Please fill in all password fields");
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordError("New passwords don't match!");
            return;
        }

        if (newPassword.length < 8) {
            setPasswordError("Password must be at least 8 characters long");
            return;
        }

        setIsUpdatingPassword(true);

        try {
            const { error: signInError } = await supabase.auth.signInWithPassword({
                email: user.email,
                password: currentPassword
            });

            if (signInError) {
                setPasswordError("Current password is incorrect");
                setIsUpdatingPassword(false);
                return;
            }

            const { error: updateError } = await supabase.auth.updateUser({
                password: newPassword
            });

            if (updateError) {
                setPasswordError(updateError.message);
            } else {
                setPasswordSuccess(true);
                setCurrentPassword("");
                setNewPassword("");
                setConfirmPassword("");

                setTimeout(() => {
                    setPasswordSuccess(false);
                }, 5000);
            }
        } catch (error) {
            console.error('Error updating password:', error);
            setPasswordError("An unexpected error occurred. Please try again.");
        } finally {
            setIsUpdatingPassword(false);
        }
    };

    // Breadcrumb for navigation
    const parentBreadcrumb = {
        name: 'Profile',
        onClick: () => navigate('/profile')
    };

    // Header actions (save/cancel buttons)
    const headerActions = (
        <div className="flex items-center gap-2">
            <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/profile')}
                disabled={isSaving}
            >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Cancel
            </Button>
            <Button
                size="sm"
                onClick={handleSaveProfile}
                disabled={isSaving}
            >
                {isSaving ? (
                    <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        Saving...
                    </>
                ) : (
                    <>
                        <Save className="h-4 w-4 mr-1" />
                        Save Changes
                    </>
                )}
            </Button>
        </div>
    );

    // Show loading state while auth is loading
    if (authLoading && !userProfile) {
        return (
            <AppShell3 pageTitle="" pageDescription="">
                <ToolHeader
                    spaceName="Account"
                    toolName="Settings"
                    currentTool="settings"
                    spaceTools={[]}
                    isGlobalView={true}
                    parentBreadcrumb={parentBreadcrumb}
                />
                <div className="flex items-center justify-center py-32">
                    <LoadingSpinner loading={true} size={20} />
                </div>
            </AppShell3>
        );
    }

    return (
        <AppShell3 pageTitle="" pageDescription="">
            <ToolHeader
                spaceName="Account"
                toolName={displayName}
                currentTool="settings"
                spaceTools={[]}
                isGlobalView={true}
                parentBreadcrumb={parentBreadcrumb}
                actions={headerActions}
            />
            <PageTransition>
                <div className="max-w-6xl mx-auto px-4 lg:px-8 py-6">
                    <div className="flex gap-8">
                        {/* Left Sidebar - Navigation */}
                        <div className="hidden lg:block w-56 flex-shrink-0">
                            <nav className="sticky top-24 space-y-1">
                                {SECTIONS.map((section) => {
                                    const Icon = section.icon;
                                    const isActive = activeSection === section.id;
                                    return (
                                        <button
                                            key={section.id}
                                            onClick={() => scrollToSection(section.id)}
                                            className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors text-left ${
                                                isActive
                                                    ? 'bg-blue-50 text-blue-700 font-medium'
                                                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                            }`}
                                        >
                                            <Icon className={`h-4 w-4 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                                            {section.label}
                                        </button>
                                    );
                                })}
                            </nav>
                        </div>

                        {/* Right Content - Scrollable Sections */}
                        <div className="flex-1 min-w-0 space-y-8">
                            {/* Profile Section */}
                            <section id="profile" className="scroll-mt-24">
                                <Card>
                                    <CardHeader>
                                        <div className="flex items-center gap-4">
                                            <div className="relative">
                                                <Avatar className="h-20 w-20">
                                                    <AvatarImage src={avatarUrl} alt={displayName} />
                                                    <AvatarFallback className="text-xl">
                                                        {getInitials(displayName)}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <label
                                                    htmlFor="avatar-upload"
                                                    className="absolute -bottom-1 -right-1 bg-primary text-primary-foreground rounded-full p-2 cursor-pointer hover:bg-primary/90 transition-colors shadow-lg"
                                                >
                                                    <Camera className="h-4 w-4" />
                                                    <input
                                                        id="avatar-upload"
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleProfileImageChange}
                                                        className="hidden"
                                                        ref={profileInputRef}
                                                        disabled={isSaving}
                                                    />
                                                </label>
                                            </div>
                                            <div className="flex-1">
                                                <CardTitle className="text-2xl">{displayName}</CardTitle>
                                                <CardDescription className="text-base mt-1">
                                                    {userEmail}
                                                </CardDescription>
                                                <div className="flex items-center gap-2 mt-2">
                                                    {previewProfile && (
                                                        <Badge variant="outline">
                                                            <Upload className="h-3 w-3 mr-1" />
                                                            New photo selected
                                                        </Badge>
                                                    )}
                                                    <Badge variant="secondary">
                                                        {userProfile?.role || 'User'}
                                                    </Badge>
                                                </div>
                                            </div>
                                        </div>
                                    </CardHeader>
                                </Card>
                            </section>

                            {/* Personal Information Section */}
                            <section id="personal" className="scroll-mt-24">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Personal Information</CardTitle>
                                        <CardDescription>
                                            Update your basic profile information
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <Label htmlFor="firstName">First Name</Label>
                                                <Input
                                                    id="firstName"
                                                    value={firstName}
                                                    onChange={(e) => setFirstName(e.target.value)}
                                                    placeholder="Enter first name"
                                                    className="mt-1.5"
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="lastName">Last Name</Label>
                                                <Input
                                                    id="lastName"
                                                    value={lastName}
                                                    onChange={(e) => setLastName(e.target.value)}
                                                    placeholder="Enter last name"
                                                    className="mt-1.5"
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="email">Email</Label>
                                                <div className="relative mt-1.5">
                                                    <Input
                                                        id="email"
                                                        value={userEmail}
                                                        disabled
                                                        className="pr-10"
                                                    />
                                                    <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                                </div>
                                            </div>
                                            <div>
                                                <Label htmlFor="jobTitle">Job Title</Label>
                                                <Input
                                                    id="jobTitle"
                                                    value={jobTitle}
                                                    onChange={(e) => setJobTitle(e.target.value)}
                                                    placeholder="Enter job title"
                                                    className="mt-1.5"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <Label htmlFor="location">Location</Label>
                                            <AddressAutocomplete
                                                value={location}
                                                onChange={setLocation}
                                                placeholder="Start typing your address..."
                                                className="mt-1.5"
                                            />
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <Label htmlFor="phoneNumber">Phone Number</Label>
                                                <Input
                                                    id="phoneNumber"
                                                    type="tel"
                                                    value={phoneNumber}
                                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                                    placeholder="e.g. +44 1234 567890"
                                                    className="mt-1.5"
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="employmentStartDate">Employment Start Date</Label>
                                                <Input
                                                    id="employmentStartDate"
                                                    type="date"
                                                    value={employmentStartDate}
                                                    onChange={(e) => setEmploymentStartDate(e.target.value)}
                                                    className="mt-1.5"
                                                />
                                            </div>
                                        </div>

                                        <Separator className="my-6" />

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <Label htmlFor="niNumber">National Insurance Number</Label>
                                                <Input
                                                    id="niNumber"
                                                    value={niNumber}
                                                    onChange={(e) => setNiNumber(e.target.value)}
                                                    placeholder="e.g. AB123456C"
                                                    className="mt-1.5"
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="dateOfBirth">Date of Birth</Label>
                                                <Input
                                                    id="dateOfBirth"
                                                    type="date"
                                                    value={dateOfBirth}
                                                    onChange={(e) => setDateOfBirth(e.target.value)}
                                                    className="mt-1.5"
                                                />
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </section>

                            {/* Emergency Contact Section */}
                            <section id="emergency" className="scroll-mt-24">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Emergency Contact</CardTitle>
                                        <CardDescription>
                                            Add a contact person in case of emergencies
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <Label htmlFor="emergencyContactName">Contact Name *</Label>
                                                <Input
                                                    id="emergencyContactName"
                                                    value={emergencyContactName}
                                                    onChange={(e) => setEmergencyContactName(e.target.value)}
                                                    placeholder="e.g. Jane Smith"
                                                    className="mt-1.5"
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="emergencyContactRelationship">Relationship *</Label>
                                                <Input
                                                    id="emergencyContactRelationship"
                                                    value={emergencyContactRelationship}
                                                    onChange={(e) => setEmergencyContactRelationship(e.target.value)}
                                                    placeholder="e.g. Spouse, Parent, Sibling"
                                                    className="mt-1.5"
                                                />
                                            </div>
                                        </div>

                                        <Separator />

                                        <div>
                                            <Label htmlFor="emergencyContactHomeAddress">Home Address</Label>
                                            <AddressAutocomplete
                                                value={emergencyContactHomeAddress}
                                                onChange={setEmergencyContactHomeAddress}
                                                placeholder="Start typing an address..."
                                                className="mt-1.5"
                                            />
                                        </div>

                                        <Separator />

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <Label htmlFor="emergencyContactHomePhone">Home Telephone</Label>
                                                <Input
                                                    id="emergencyContactHomePhone"
                                                    type="tel"
                                                    value={emergencyContactHomePhone}
                                                    onChange={(e) => setEmergencyContactHomePhone(e.target.value)}
                                                    placeholder="e.g. 01234 567890"
                                                    className="mt-1.5"
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="emergencyContactMobilePhone">Mobile Number *</Label>
                                                <Input
                                                    id="emergencyContactMobilePhone"
                                                    type="tel"
                                                    value={emergencyContactMobilePhone}
                                                    onChange={(e) => setEmergencyContactMobilePhone(e.target.value)}
                                                    placeholder="e.g. 07123 456789"
                                                    className="mt-1.5"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <Label htmlFor="emergencyContactEmail">Email Address</Label>
                                            <Input
                                                id="emergencyContactEmail"
                                                type="email"
                                                value={emergencyContactEmail}
                                                onChange={(e) => setEmergencyContactEmail(e.target.value)}
                                                placeholder="e.g. emergency@example.com"
                                                className="mt-1.5"
                                            />
                                        </div>

                                        <Separator />

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <Label htmlFor="emergencyContactEmployer">Employer</Label>
                                                <Input
                                                    id="emergencyContactEmployer"
                                                    value={emergencyContactEmployer}
                                                    onChange={(e) => setEmergencyContactEmployer(e.target.value)}
                                                    placeholder="e.g. Company Name"
                                                    className="mt-1.5"
                                                />
                                            </div>
                                            <div>
                                                <Label htmlFor="emergencyContactWorkPhone">Work Telephone</Label>
                                                <Input
                                                    id="emergencyContactWorkPhone"
                                                    type="tel"
                                                    value={emergencyContactWorkPhone}
                                                    onChange={(e) => setEmergencyContactWorkPhone(e.target.value)}
                                                    placeholder="e.g. 020 1234 5678"
                                                    className="mt-1.5"
                                                />
                                            </div>
                                        </div>

                                        <p className="text-xs text-gray-500 mt-2">
                                            * Required fields. This information will be accessible to authorized team members in case of emergencies.
                                        </p>
                                    </CardContent>
                                </Card>
                            </section>

                            {/* ID Documents Section */}
                            <section id="documents" className="scroll-mt-24">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>ID Documents & Additional Information</CardTitle>
                                        <CardDescription>
                                            Passport, driving license, medical info, and right to work
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        {/* Passport */}
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <Label htmlFor="passportNumber">Passport Number</Label>
                                                    <Input
                                                        id="passportNumber"
                                                        value={passportNumber}
                                                        onChange={(e) => setPassportNumber(e.target.value)}
                                                        placeholder="Passport number"
                                                        className="mt-1.5"
                                                    />
                                                </div>
                                                <div>
                                                    <Label htmlFor="passportExpiry">Passport Expiry Date</Label>
                                                    <Input
                                                        id="passportExpiry"
                                                        type="date"
                                                        value={passportExpiry}
                                                        onChange={(e) => setPassportExpiry(e.target.value)}
                                                        className="mt-1.5"
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <Label htmlFor="passportFile">Passport Document</Label>
                                                {(passportFileUrl || passportFilePreview) && (
                                                    <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm mt-1.5">
                                                        <span className="text-blue-900">
                                                            {passportFilePreview ? `New: ${passportFilePreview.name}` : 'Document uploaded'}
                                                        </span>
                                                        {passportFileUrl && !passportFilePreview && (
                                                            <a
                                                                href={passportFileUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="ml-2 text-blue-600 hover:underline"
                                                            >
                                                                View
                                                            </a>
                                                        )}
                                                    </div>
                                                )}
                                                <div className="flex gap-2 mt-1.5">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        onClick={() => passportInputRef.current?.click()}
                                                        className="flex-1"
                                                    >
                                                        <Upload className="h-4 w-4 mr-2" />
                                                        {passportFileUrl || passportFilePreview ? 'Replace Document' : 'Upload Document'}
                                                    </Button>
                                                    <input
                                                        ref={passportInputRef}
                                                        type="file"
                                                        accept=".pdf,.jpg,.jpeg,.png"
                                                        className="hidden"
                                                        onChange={(e) => handleDocumentUpload(e, 'passport')}
                                                    />
                                                </div>
                                                <p className="text-xs text-gray-500 mt-1">PDF or image files (max 10MB)</p>
                                            </div>
                                        </div>

                                        <Separator />

                                        {/* Driving License */}
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <Label htmlFor="drivingLicenseNumber">Driving License Number</Label>
                                                    <Input
                                                        id="drivingLicenseNumber"
                                                        value={drivingLicenseNumber}
                                                        onChange={(e) => setDrivingLicenseNumber(e.target.value)}
                                                        placeholder="Driving license number"
                                                        className="mt-1.5"
                                                    />
                                                </div>
                                                <div>
                                                    <Label htmlFor="drivingLicenseExpiry">License Expiry Date</Label>
                                                    <Input
                                                        id="drivingLicenseExpiry"
                                                        type="date"
                                                        value={drivingLicenseExpiry}
                                                        onChange={(e) => setDrivingLicenseExpiry(e.target.value)}
                                                        className="mt-1.5"
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <Label htmlFor="licenseFile">Driving License Document</Label>
                                                {(drivingLicenseFileUrl || drivingLicenseFilePreview) && (
                                                    <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm mt-1.5">
                                                        <span className="text-blue-900">
                                                            {drivingLicenseFilePreview ? `New: ${drivingLicenseFilePreview.name}` : 'Document uploaded'}
                                                        </span>
                                                        {drivingLicenseFileUrl && !drivingLicenseFilePreview && (
                                                            <a
                                                                href={drivingLicenseFileUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="ml-2 text-blue-600 hover:underline"
                                                            >
                                                                View
                                                            </a>
                                                        )}
                                                    </div>
                                                )}
                                                <div className="flex gap-2 mt-1.5">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        onClick={() => drivingLicenseInputRef.current?.click()}
                                                        className="flex-1"
                                                    >
                                                        <Upload className="h-4 w-4 mr-2" />
                                                        {drivingLicenseFileUrl || drivingLicenseFilePreview ? 'Replace Document' : 'Upload Document'}
                                                    </Button>
                                                    <input
                                                        ref={drivingLicenseInputRef}
                                                        type="file"
                                                        accept=".pdf,.jpg,.jpeg,.png"
                                                        className="hidden"
                                                        onChange={(e) => handleDocumentUpload(e, 'license')}
                                                    />
                                                </div>
                                                <p className="text-xs text-gray-500 mt-1">PDF or image files (max 10MB)</p>
                                            </div>
                                        </div>

                                        <Separator />

                                        {/* Right to Work */}
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <Label htmlFor="rightToWorkStatus">Right to Work Status</Label>
                                                    <Input
                                                        id="rightToWorkStatus"
                                                        value={rightToWorkStatus}
                                                        onChange={(e) => setRightToWorkStatus(e.target.value)}
                                                        placeholder="e.g. UK Citizen, Work Visa"
                                                        className="mt-1.5"
                                                    />
                                                </div>
                                                <div>
                                                    <Label htmlFor="rightToWorkExpiry">Right to Work Expiry (if applicable)</Label>
                                                    <Input
                                                        id="rightToWorkExpiry"
                                                        type="date"
                                                        value={rightToWorkExpiry}
                                                        onChange={(e) => setRightToWorkExpiry(e.target.value)}
                                                        className="mt-1.5"
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <Label htmlFor="rtwFile">Right to Work Document</Label>
                                                {(rightToWorkFileUrl || rightToWorkFilePreview) && (
                                                    <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm mt-1.5">
                                                        <span className="text-blue-900">
                                                            {rightToWorkFilePreview ? `New: ${rightToWorkFilePreview.name}` : 'Document uploaded'}
                                                        </span>
                                                        {rightToWorkFileUrl && !rightToWorkFilePreview && (
                                                            <a
                                                                href={rightToWorkFileUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="ml-2 text-blue-600 hover:underline"
                                                            >
                                                                View
                                                            </a>
                                                        )}
                                                    </div>
                                                )}
                                                <div className="flex gap-2 mt-1.5">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        onClick={() => rightToWorkInputRef.current?.click()}
                                                        className="flex-1"
                                                    >
                                                        <Upload className="h-4 w-4 mr-2" />
                                                        {rightToWorkFileUrl || rightToWorkFilePreview ? 'Replace Document' : 'Upload Document'}
                                                    </Button>
                                                    <input
                                                        ref={rightToWorkInputRef}
                                                        type="file"
                                                        accept=".pdf,.jpg,.jpeg,.png"
                                                        className="hidden"
                                                        onChange={(e) => handleDocumentUpload(e, 'rightToWork')}
                                                    />
                                                </div>
                                                <p className="text-xs text-gray-500 mt-1">PDF or image files (max 10MB)</p>
                                            </div>
                                        </div>

                                        <Separator />

                                        {/* Medical Information */}
                                        <div>
                                            <Label htmlFor="medicalInfo">Medical Information</Label>
                                            <Textarea
                                                id="medicalInfo"
                                                value={medicalInfo}
                                                onChange={(e) => setMedicalInfo(e.target.value)}
                                                placeholder="Any medical conditions, allergies, or important health information..."
                                                rows={4}
                                                className="mt-1.5"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">
                                                This information is confidential and only accessible to authorized personnel
                                            </p>
                                        </div>
                                    </CardContent>
                                </Card>
                            </section>

                            {/* Notifications Section */}
                            <section id="notifications" className="scroll-mt-24">
                                <NotificationPreferences />
                                <div className="mt-6">
                                    <AlertMutingCard />
                                </div>
                            </section>

                            {/* Security Section */}
                            <section id="security" className="scroll-mt-24 pb-8">
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Change Password</CardTitle>
                                        <CardDescription>
                                            Update your password to keep your account secure
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        {passwordSuccess && (
                                            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                                                <CheckCircle className="h-5 w-5 text-green-600" />
                                                <span className="text-sm text-green-800">Password updated successfully!</span>
                                            </div>
                                        )}

                                        {passwordError && (
                                            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                                <XCircle className="h-5 w-5 text-red-600" />
                                                <span className="text-sm text-red-800">{passwordError}</span>
                                            </div>
                                        )}

                                        <div>
                                            <Label htmlFor="currentPassword">Current Password</Label>
                                            <Input
                                                id="currentPassword"
                                                type="password"
                                                value={currentPassword}
                                                onChange={(e) => {
                                                    setCurrentPassword(e.target.value);
                                                    setPasswordError("");
                                                }}
                                                placeholder="Enter your current password"
                                                disabled={isUpdatingPassword}
                                                className="mt-1.5"
                                            />
                                        </div>

                                        <div>
                                            <Label htmlFor="newPassword">New Password</Label>
                                            <Input
                                                id="newPassword"
                                                type="password"
                                                value={newPassword}
                                                onChange={(e) => {
                                                    setNewPassword(e.target.value);
                                                    setPasswordError("");
                                                }}
                                                placeholder="Enter your new password"
                                                disabled={isUpdatingPassword}
                                                className="mt-1.5"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">Must be at least 8 characters long</p>
                                        </div>

                                        <div>
                                            <Label htmlFor="confirmPassword">Confirm New Password</Label>
                                            <Input
                                                id="confirmPassword"
                                                type="password"
                                                value={confirmPassword}
                                                onChange={(e) => {
                                                    setConfirmPassword(e.target.value);
                                                    setPasswordError("");
                                                }}
                                                placeholder="Confirm your new password"
                                                disabled={isUpdatingPassword}
                                                className="mt-1.5"
                                            />
                                        </div>

                                        <Button
                                            onClick={handlePasswordChange}
                                            className="w-full sm:w-auto"
                                            disabled={isUpdatingPassword}
                                        >
                                            <Key className="h-4 w-4 mr-2" />
                                            {isUpdatingPassword ? 'Updating...' : 'Update Password'}
                                        </Button>
                                    </CardContent>
                                </Card>
                            </section>

                            {/* Kiosk PIN Section */}
                            <section id="kiosk-pin" className="scroll-mt-24 pb-8">
                                <KioskPinManager
                                    userId={userProfile?.id}
                                    currentPin={userProfile?.kiosk_pin}
                                    companyId={company?.id}
                                    onPinChange={() => {
                                        // Refresh user profile to get updated PIN
                                        refreshUserData();
                                    }}
                                />
                            </section>
                        </div>
                    </div>
                </div>
            </PageTransition>

            {/* Profile Image Cropper */}
            <ImageCropper
                isOpen={showProfileCropper}
                onClose={() => {
                    setShowProfileCropper(false);
                    setTempProfileImage(null);
                }}
                imageSrc={tempProfileImage}
                onCropComplete={(croppedImage) => {
                    handleProfileCropComplete(croppedImage);
                    setShowProfileCropper(false);
                    setTempProfileImage(null);
                }}
                aspectRatio={1}
                cropShape="round"
            />
        </AppShell3>
    );
}
